/**
 * HttpClient — Phase 4-2 / T9.
 *
 * Minimal fetch wrapper used by all TanStack queries / mutations.
 *
 * Importers: src/lib/api/queries.ts, src/lib/api/mutations.ts (call http.get /
 * http.post). src/App.svelte initializes baseUrl from VITE_API_BASE.
 * src/lib/embed/bootstrap.ts sets the session token after SSO.
 */

class HttpClient {
  baseUrl = '';
  sessionToken: string | null = null;

  setBaseUrl(u: string): void {
    this.baseUrl = u;
  }

  setSessionToken(t: string | null): void {
    this.sessionToken = t;
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body);
  }

  async delete<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('DELETE', path, body);
  }

  /**
   * POST raw bytes (an image the operator picked) instead of JSON.
   *
   * Same base URL, same session token, same credentials as every other call —
   * only the encoding differs, because the server stores these bytes rather
   * than reading fields out of them. Kept as its own method rather than a
   * flag on `post` so no caller can accidentally send an object here and have
   * it stored as a file.
   */
  async postBytes<T>(path: string, body: Blob, contentType: string): Promise<T> {
    const headers: Record<string, string> = { 'content-type': contentType };
    if (this.sessionToken) headers['X-Widget-Auth'] = this.sessionToken;

    const res = await fetch(this.baseUrl + path, {
      method: 'POST',
      headers,
      body,
      credentials: 'include',
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`API POST ${path} failed: ${res.status} ${text}`);
    }
    return JSON.parse(await res.text()) as T;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (this.sessionToken) headers['X-Widget-Auth'] = this.sessionToken;

    const res = await fetch(this.baseUrl + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`API ${method} ${path} failed: ${res.status} ${text}`);
    }

    // 204 No Content (and other empty bodies) — return undefined rather than
    // attempting JSON.parse on an empty string. Callers that type the response
    // as `void` (e.g. fire-and-forget mutations like sender-action, react)
    // ignore the value; typed callers don't hit 204.
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    if (text.length === 0) return undefined as T;
    return JSON.parse(text) as T;
  }
}

export const http = new HttpClient();
